import roles from './roles/en-roles';
export default {
    app: {
        title: 'Blood on the Clocktower',
        subtitle: 'Trouble Brewing',
    },
    nav: {
        overview: 'Overview',
        myRole: 'My Role',
        rules: 'Rules',
        info: 'Info',
    },
    common: {
        search: 'Search...',
        filter: 'Filter',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        select: 'Select',
        notes: 'Notes',
        day: 'Day',
        night: 'Night',
    },
    roleTypes: {
        townsfolk: 'Townsfolk',
        outsider: 'Outsider',
        minion: 'Minion',
        demon: 'Demon',
    },
    overview: {
        title: 'Role Overview',
        filterByType: 'Filter by type',
        allRoles: 'All Roles',
    },
    myRole: {
        title: 'My Role',
        selectRole: 'Select your role',
        noRoleSelected: 'No role selected',
        ability: 'Ability',
        relevantRules: 'Relevant Rules',
        myNotes: 'My Notes',
        writeNote: 'Add note...',
    },
    gameTracker: {
        title: 'Game Tracker',
        currentDay: 'Current Day',
        phase: 'Phase',
        players: 'Players',
        addPlayer: 'Add Player',
        playerName: 'Player Name',
        alive: 'Alive',
        dead: 'Dead',
        usedAbility: 'Ability Used',
    },
    rules: {
        title: 'Game Rules',
        fullRules: 'Full Rules',
        overview: {
            title: 'Overview',
            description: 'Blood on the Clocktower is a social deduction game for 5-15 players. In "Trouble Brewing", good townsfolk fight against evil, represented by demons and their minions.',
        },
        setup: {
            title: 'Game Setup',
            content: `
        <p><strong>Role Distribution:</strong></p>
        <ul>
          <li>The Storyteller distributes roles secretly to all players</li>
          <li>Each player receives a role and learns their ability</li>
          <li>The composition depends on the player count</li>
        </ul>
        <p><strong>First Night:</strong></p>
        <ul>
          <li>All players close their eyes</li>
          <li>The Storyteller wakes individual roles in a specific order</li>
          <li>Each role performs their night action</li>
        </ul>
      `,
        },
        gameplay: {
            title: 'Gameplay',
            content: `
        <p>The game alternates between day and night phases:</p>
        <p><strong>Day Phase:</strong></p>
        <ul>
          <li>All living players discuss and exchange information</li>
          <li>Players can nominate and vote</li>
          <li>The nominated player with the most votes is executed</li>
          <li>A dead player may vote once more in the game</li>
        </ul>
        <p><strong>Night Phase:</strong></p>
        <ul>
          <li>The Demon kills a player</li>
          <li>Various roles use their abilities</li>
          <li>In the morning, it's announced who died</li>
        </ul>
      `,
        },
        winning: {
            title: 'Win Conditions',
            content: `
        <p><strong>Good wins when:</strong></p>
        <ul>
          <li>The Demon is executed (by vote during the day)</li>
        </ul>
        <p><strong>Evil wins when:</strong></p>
        <ul>
          <li>Only 2 players remain alive (1 Demon + 1 other)</li>
          <li>Or through special abilities of certain evil roles</li>
        </ul>
      `,
        },
        abilities: {
            title: 'Abilities',
            content: `
        <p><strong>Types of Abilities:</strong></p>
        <ul>
          <li><strong>Passive Abilities:</strong> Work automatically</li>
          <li><strong>Night Abilities:</strong> Used during the night</li>
          <li><strong>Day Abilities:</strong> Can be used during the day</li>
          <li><strong>One-time Abilities:</strong> Can only be used once per game</li>
        </ul>
        <p><strong>Important:</strong></p>
        <ul>
          <li>Dead players lose their active abilities</li>
          <li>The Storyteller may give false information when game mechanics require it</li>
          <li>Some abilities interact with each other</li>
        </ul>
      `,
        },
        washerwoman: {
            1: 'The Washerwoman learns two players in the first night, one of whom has a specific good role.',
            2: 'This information can be false if drunk or poisoned.',
        },
        librarian: {
            1: 'The Librarian learns two players in the first night, one of whom is an Outsider.',
            2: 'If no Outsider is in the game, the Librarian learns a "0".',
        },
        investigator: {
            1: 'The Investigator learns two players in the first night, one of whom is a Minion.',
            2: 'This information can be false if drunk or poisoned.',
        },
        chef: {
            1: 'Evil neighbors count as a pair. The Chef learns the number of all such pairs.',
        },
        empath: {
            1: 'The Empath only learns the number of evil living neighbors, not their identity.',
        },
        fortuneteller: {
            1: 'The Fortune Teller only learns "Yes" or "No", not which of the two players is the Demon.',
            2: 'The red herring is a good player who always registers as the Demon, even when dead.',
        },
        undertaker: {
            1: 'The Undertaker learns the true role, not the pretended one.',
        },
        monk: {
            1: 'The Monk cannot protect themselves. Protected players can still die by execution.',
        },
        ravenkeeper: {
            1: 'The Ravenkeeper is only woken if they die at night, not during day execution.',
        },
        virgin: {
            1: 'After the first nomination, the Virgin loses their ability.',
        },
        slayer: {
            1: 'The Slayer can only use their ability once per game, regardless of success.',
        },
        soldier: {
            1: 'The Soldier can die by execution or by abilities of other evil players.',
        },
        mayor: {
            1: 'The Mayor enables a timeout victory if no execution occurs.',
        },
        butler: {
            1: 'The Butler may not vote if their master does not vote.',
            2: 'The Butler chooses a new master each night.',
        },
        drunk: {
            1: 'The Drunk thinks they are another role. The Storyteller treats them as this role.',
            2: 'All information the Drunk receives is false or arbitrary.',
        },
        recluse: {
            1: 'The Recluse can appear as evil when abilities check for alignment.',
        },
        saint: {
            1: 'If the Saint is executed, the game ends immediately with a loss for good.',
        },
        poisoner: {
            1: 'Poisoned players receive false information or no effect from their abilities.',
            2: 'Poisoning ends automatically the next dusk.',
        },
        spy: {
            1: 'The Spy sees all information in the Grimoire and all tokens.',
            2: 'The Spy can register as good when abilities check for alignment.',
        },
        scarletwoman: {
            1: 'The Scarlet Woman only becomes the Demon if 5+ players are alive.',
            2: 'When she becomes the Demon, she completely loses her old ability.',
        },
        baron: {
            1: 'The Baron adds 2 additional Outsiders to the setup (replaces Townsfolk).',
        },
        imp: {
            1: 'The Imp can kill themselves to make a Minion the new Imp.',
            2: 'When the Imp kills themselves, they do not die, but a Minion becomes the Imp.',
            3: 'The Imp cannot kill a player with the Soldier ability.',
        },
    },
    info: {
        title: 'Information',
        version: 'Version',
        about: 'About this app',
        aboutText: 'This app supports you while playing Blood on the Clocktower in the "Trouble Brewing" scenario. It provides role descriptions, rules, and a game tracker.',
        language: 'Language',
        credits: {
            title: 'Credits',
            game: 'Blood on the Clocktower is a game by Steven Medway and The Pandemonium Institute.',
            design: 'This app was created as a fan project and is not officially licensed.',
        },
    },
    roles,
};
